import { Injectable } from '@angular/core'
import { Actions, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  take
} from 'rxjs/operators'
import {
  selectCurrentUserId,
  selectCurrentUserName
} from '../auth/auth.selectors'
import * as fromUserActions from '../../features/user-store/user.actions'
import * as fromUsersSelectors from '../../features/user-store/user.selectors'

@Injectable({ providedIn: 'root' })
export class ProfilingService {
  // Becomes true once successLoadUsers has fired at least once in this session
  private readonly usersLoadedOnce$ = new BehaviorSubject<boolean>(false)

  /**
   * Emits true once the current user's profile is complete (has a name).
   * Waits until the user list has been fetched before deciding, so brand-new
   * users (absent from the entity store) correctly receive false instead of
   * blocking the observable forever.
   */
  public readonly isProfileCompleted$: Observable<boolean> = combineLatest([
    this.store.pipe(select(selectCurrentUserId)),
    this.store.pipe(select(selectCurrentUserName)),
    this.usersLoadedOnce$.pipe(filter((loaded) => loaded))
  ]).pipe(
    switchMap(([userId, authName]) =>
      this.store
        .pipe(select(fromUsersSelectors.getUserByIdFactory(userId)))
        .pipe(map((user) => ({ user, authName })))
    ),
    map(({ user, authName }) => {
      const hasUserStoreName = user != null && !!user.name
      const hasAuthName = !!authName
      return hasUserStoreName || hasAuthName
    }),
    distinctUntilChanged(),
    shareReplay(1)
  )

  constructor(private store: Store, private actions$: Actions) {
    this.actions$
      .pipe(ofType(fromUserActions.successLoadUsers), take(1))
      .subscribe(() => this.usersLoadedOnce$.next(true))
  }
}
